import { TokenAmount, TokenLabel } from "@/components/brand/TokenAmount";
import { TokenIcon } from "@/components/brand/TokenIcon";
import { ConfirmSheet } from "@/components/defi/ConfirmSheet";
import type { UpdateStage } from "@/components/defi/UpdateStageLoader";
import { RangeSlider } from "@/components/ui/RangeSlider";
import { CollateralLines } from "@/components/vault/CollateralLines";
import { RatioGauge } from "@/components/vault/RatioGauge";
import {
	useGetPublicPriceFeeds,
	useProtocolRiskParams,
	toRiskParams,
} from "@/hooks/usePublicProtocol";
import { useVaultDetail } from "@/hooks/useUserVaults";
import { useAuth } from "@/lib/auth";
import {
	depositableTokenMax,
	mintableUsdmMax,
	repayableUsdmMax,
	withdrawableTokenMax,
} from "@/lib/amount-limits";
import { maxLtvFromCr } from "@/lib/defi-math";
import {
	closeEmptyVault,
	depositCollateral,
	mintUsdm,
	repayUsdm,
	withdrawCollateral,
	type CollateralKey,
} from "@motokomaker/ic-client";
import { ensureProtocolUser } from "@/lib/ensure-user";
import { getIcConfig } from "@/lib/network";
import { fireMintConfetti } from "@/lib/mint-confetti";
import { ROUTES } from "@/lib/routes";
import {
	cn,
	formatAmountPlain,
	formatNum,
	formatUsd,
	parseAmount,
} from "@/lib/utils";
import {
	ArrowDownToLine,
	Flame,
	Gavel,
	Landmark,
	Plus,
	Trash2,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { classifyVaultRisk } from "@/lib/vault-risk";

export type VaultActionKind = "deposit" | "mint" | "repay" | "withdraw" | null;

const COLS: CollateralKey[] = ["ICP", "ckBTC", "ckETH"];

/**
 * Vault detail + actions — used in page and popup sheet.
 */
export function VaultDetailPanel({
	vaultId,
	initialAction = null,
	/** When undefined, derived from vault.isOwner after load. */
	canOperate: canOperateProp,
	compact = false,
}: {
	vaultId: string;
	initialAction?: VaultActionKind;
	canOperate?: boolean;
	compact?: boolean;
}) {
	const { data: vault, isLoading } = useVaultDetail(vaultId);
	const {
		isAuthenticated,
		principal,
		balances,
		refreshBalances,
		ensureProtocolActor,
	} = useAuth();
	const canOperate =
		canOperateProp ??
		(vault?.isOwner === true ||
			(!!principal &&
				!!vault?.owner &&
				principal === vault.owner));
	const navigate = useNavigate();
	const { data: priceData } = useGetPublicPriceFeeds();
	const { data: riskData } = useProtocolRiskParams();
	const riskParams = toRiskParams(riskData);
	const minCr = riskParams.minCollateralRatio;
	const liqT = riskParams.liquidationThreshold;
	const maxMintLtv = maxLtvFromCr(minCr);
	const qc = useQueryClient();

	const [action, setAction] = useState<VaultActionKind>(null);
	const [amount, setAmount] = useState("");
	const [collateral, setCollateral] = useState<CollateralKey>("ICP");
	const [stage, setStage] = useState<UpdateStage>("idle");
	const [pending, setPending] = useState(false);
	const [closeOpen, setCloseOpen] = useState(false);
	const [closing, setClosing] = useState(false);

	// Open action modal when navigating with ?action= (create vault → deposit)
	useEffect(() => {
		if (initialAction && canOperate) {
			setAction(initialAction);
			setAmount("");
			setStage("idle");
		}
	}, [initialAction, vaultId, canOperate]);

	const n = parseAmount(amount);
	const prices: Record<string, number> = priceData?.prices ?? {
		ICP: 0,
		ckBTC: 0,
		ckETH: 0,
	};

	const maxWithdrawToken = useMemo(() => {
		if (!vault || !vault.raw) return 0;
		const price = prices[collateral] ?? 0;
		if (price <= 0) return 0;
		const entry = vault.raw.collateral.find(([k]) => k === collateral);
		const tokenBal = entry?.[1] ?? 0;
		if (tokenBal <= 0) return 0;
		const minColUsd = vault.debtUsdm * minCr;
		const freeUsd = Math.max(0, vault.collateralUsd - minColUsd);
		const freeToken = freeUsd / price;
		return withdrawableTokenMax(Math.min(tokenBal, freeToken));
	}, [vault, collateral, prices, minCr]);

	const preview = useMemo(() => {
		if (!vault) return null;
		let col = vault.collateralUsd;
		let debt = vault.debtUsdm;
		const p = prices[collateral] ?? 0;
		if (action === "deposit") col += n * p;
		else if (action === "withdraw") col = Math.max(0, col - n * p);
		else if (action === "mint") debt += n;
		else if (action === "repay") debt = Math.max(0, debt - n);
		const ltv = col > 0 ? (debt / col) * 100 : 0;
		const hf = debt > 0 ? col / debt : Number.POSITIVE_INFINITY;
		return { col, debt, ltv, hf };
	}, [vault, action, n, collateral, prices]);

	const walletColRaw =
		collateral === "ICP"
			? balances.ICP
			: collateral === "ckBTC"
				? balances.ckBTC
				: balances.ckETH;
	const walletCol = depositableTokenMax(walletColRaw);
	const colDecimals = collateral === "ckBTC" ? 6 : 4;

	const maxMint = vault
		? mintableUsdmMax(vault.collateralUsd, vault.debtUsdm, minCr)
		: 0;
	const maxRepay = vault
		? repayableUsdmMax(vault.debtUsdm, balances.USDM)
		: 0;

	/** Empty = 0 debt and no remaining collateral tokens (safe to delete). */
	const isEmptyVault = useMemo(() => {
		if (!vault) return false;
		if (vault.debtUsdm > 1e-9) return false;
		const cols = vault.raw?.collateral ?? [];
		if (cols.length === 0) return vault.collateralUsd < 1e-6;
		return cols.every(([, amt]) => amt <= 0);
	}, [vault]);

	/** After this withdraw/repay, position would be empty → offer close. */
	const willBeEmptyAfter = useMemo(() => {
		if (!vault || !preview) return false;
		if (action !== "withdraw" && action !== "repay") return false;
		if (preview.debt > 1e-9) return false;
		if (action === "repay") {
			const cols = vault.raw?.collateral ?? [];
			const hasColl =
				cols.some(([, amt]) => amt > 0) || vault.collateralUsd > 1e-6;
			return !hasColl;
		}
		// withdraw: all lines zero after this pull (single-asset max case)
		const cols = vault.raw?.collateral ?? [];
		let remainingUsd = 0;
		for (const [k, amt] of cols) {
			const left =
				k === collateral ? Math.max(0, amt - n) : amt;
			if (left > 0) remainingUsd += left * (prices[k] ?? 0);
		}
		return remainingUsd < 1e-6 && preview.debt <= 1e-9;
	}, [vault, preview, action, collateral, n, prices]);

	const pctOf = (current: number, max: number) =>
		max > 0 ? Math.min(100, Math.max(0, (current / max) * 100)) : 0;

	const invalidate = () => {
		void qc.invalidateQueries({ queryKey: ["v2", "vault"] });
		void qc.invalidateQueries({ queryKey: ["v2", "userVaults"] });
		void qc.invalidateQueries({ queryKey: ["v2", "public", "vaults"] });
		void qc.invalidateQueries({ queryKey: ["v2", "certifiedAnalytics"] });
		void qc.invalidateQueries({ queryKey: ["v2", "wallet", "activity"] });
		void refreshBalances();
	};

	async function runCloseEmpty() {
		if (!vault || vault.source !== "live") {
			toast.error("Live protocol required");
			return;
		}
		setClosing(true);
		try {
			const pair = await ensureProtocolActor();
			if (!pair) {
				toast.error("Protocol actor not ready");
				return;
			}
			await ensureProtocolUser(pair.actor);
			await closeEmptyVault(pair.actor, vault.id);
			toast.success(`Vault #${vault.id.toString()} closed`);
			setCloseOpen(false);
			invalidate();
			navigate(ROUTES.vaults);
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Close failed");
		} finally {
			setClosing(false);
		}
	}

	async function runAction() {
		if (!vault || !amount) return;
		if (!Number.isFinite(n) || n <= 0) {
			toast.error("Enter a valid amount");
			return;
		}
		if (action === "deposit" && n > walletCol + 1e-12) {
			toast.error("Amount exceeds spendable wallet balance (after transfer fee)");
			return;
		}
		if (action === "withdraw" && n > maxWithdrawToken + 1e-12) {
			toast.error("Amount exceeds free collateral (CR-capped)");
			return;
		}
		if (action === "mint" && n > maxMint + 1e-12) {
			toast.error("Amount exceeds max mint (fee / CR buffer applied)");
			return;
		}
		if (action === "repay" && n > maxRepay + 1e-12) {
			toast.error("Amount exceeds max repay (wallet fee reserved)");
			return;
		}
		if (vault.source !== "live") {
			toast.error("Live protocol required — check canister env and Connect");
			return;
		}
		setPending(true);
		setStage("signing");
		const offerClose = willBeEmptyAfter;
		try {
			const pair = await ensureProtocolActor();
			if (!pair) {
				toast.error("Live protocol required — check canister env and Connect");
				setStage("idle");
				return;
			}
			await ensureProtocolUser(pair.actor);
			const config = getIcConfig();
			setStage("submitting");
			if (action === "deposit") {
				await depositCollateral(pair.actor, pair.agent, config, {
					vaultId: vault.id,
					collateralType: collateral,
					amount: n,
				});
			} else if (action === "withdraw") {
				await withdrawCollateral(pair.actor, vault.id, collateral, n);
			} else if (action === "mint") {
				await mintUsdm(pair.actor, vault.id, n);
				fireMintConfetti();
			} else if (action === "repay") {
				await repayUsdm(pair.actor, pair.agent, config, vault.id, n);
			}
			setStage("confirmed");
			if (offerClose) {
				toast.success("Position empty — close vault to remove it from lists");
			} else if (action === "repay" && preview && preview.debt <= 1e-9) {
				toast.success(
					"Debt repaid — withdraw free collateral, then close the empty vault",
				);
			} else {
				toast.success("Update confirmed on-chain");
			}
			invalidate();
			setTimeout(() => {
				setAction(null);
				setAmount("");
				setStage("idle");
				if (offerClose) setCloseOpen(true);
			}, 600);
		} catch (err) {
			setStage("idle");
			toast.error(err instanceof Error ? err.message : "Update failed");
		} finally {
			setPending(false);
		}
	}

	function openAction(kind: VaultActionKind) {
		if (!canOperate) {
			toast.message("View only — connect as owner to manage");
			return;
		}
		if (!isAuthenticated) {
			toast.message("Connect Internet Identity to continue");
			return;
		}
		setAction(kind);
		setAmount("");
		setStage("idle");
		if (vault?.raw?.collateral?.[0]) {
			setCollateral(vault.raw.collateral[0][0]);
		}
	}

	if (isLoading) {
		return (
			<div className="space-y-4 p-4">
				<div className="skeleton-shimmer mx-auto h-40 w-40 rounded-full" />
				<div className="skeleton-shimmer h-20" />
			</div>
		);
	}

	if (!vault) {
		return (
			<div className="p-6 text-center text-sm text-muted-foreground">
				Vault not found on-chain.
			</div>
		);
	}

	const actions = [
		{ key: "deposit" as const, label: "Deposit", desc: "Add collateral", icon: Plus },
		{
			key: "withdraw" as const,
			label: "Withdraw",
			desc: "Free collateral",
			icon: ArrowDownToLine,
		},
		{ key: "mint" as const, label: "Mint", desc: "Borrow USDM", icon: Landmark },
		{ key: "repay" as const, label: "Repay", desc: "Burn debt", icon: Flame },
	];

	const titleFor = (a: VaultActionKind) => {
		if (a === "deposit") return "Deposit collateral";
		if (a === "withdraw") return "Withdraw free collateral";
		if (a === "mint") return "Borrow more (mint USDM)";
		if (a === "repay") return "Repay debt";
		return "Vault action";
	};

	const descFor = (a: VaultActionKind) => {
		if (a === "deposit") return "Approve ICRC transfer then deposit into this vault.";
		if (a === "withdraw")
			return "Withdraw free collateral while keeping CR ≥ liquidation minimum.";
		if (a === "mint") return "Mint USDM against free collateral capacity.";
		if (a === "repay") return "Approve USDM then repay vault debt.";
		return "";
	};

	return (
		<div className={compact ? "space-y-4" : "space-y-8"}>
			<section
				id="section-overview"
				className={cn(
					"flex flex-col items-center scroll-mt-24 rounded-2xl py-2",
					classifyVaultRisk(vault.collateralUsd, vault.debtUsdm, riskParams)
						.ringClass,
				)}
			>
				<p className="section-eyebrow">Vault #{vault.id.toString()}</p>
				<p className="text-xs text-muted-foreground">
					{vault.collateralLabel} · on-chain · HF{" "}
					{Number.isFinite(vault.healthFactor)
						? `${vault.healthFactor.toFixed(2)}×`
						: "∞"}
				</p>
				<div className="mt-3">
					<RatioGauge
						collateralUsd={vault.collateralUsd}
						debtUsdm={vault.debtUsdm}
						ltvPct={vault.ltvPct}
						healthFactor={vault.healthFactor}
						liquidationCr={liqT}
						minCr={minCr}
						size={compact ? 140 : 200}
					/>
				</div>
				<div
					id="section-position"
					className="mt-4 grid w-full max-w-md grid-cols-2 gap-3 scroll-mt-24 px-2 text-center text-sm"
				>
					<div className="rounded-xl border border-border/60 bg-muted/30 px-3 py-3">
						<p className="text-[10px] uppercase text-muted-foreground">
							Collateral
						</p>
						<p className="font-mono-amount font-bold">
							{formatUsd(vault.collateralUsd, 0)}
						</p>
					</div>
					<div className="rounded-xl border border-border/60 bg-muted/30 px-3 py-3">
						<p className="text-[10px] uppercase text-muted-foreground">Debt</p>
						<div className="flex justify-center">
							<TokenAmount id="USDM" value={vault.debtUsdm} decimals={2} size="sm" />
						</div>
					</div>
				</div>
				<div className="mt-4 w-full max-w-md px-2 text-left">
					<p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
						Collateral locked
					</p>
					<CollateralLines
						lines={(vault.raw?.collateral ?? []).map(([asset, amount]) => {
							const priceUsd = prices[asset] ?? 0;
							return {
								asset,
								amount,
								priceUsd,
								valueUsd: amount * priceUsd,
							};
						})}
					/>
				</div>
			</section>

			{canOperate ? (
				<section
					id="section-actions"
					className="scroll-mt-24 space-y-2"
				>
					<p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
						Actions
					</p>
					<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
						{actions.map((a) => {
							const Icon = a.icon;
							return (
								<button
									key={a.key}
									type="button"
									className={
										a.key === "mint"
											? "btn-primary flex flex-col items-start gap-0.5 px-3 py-3 text-left"
											: "btn-secondary flex flex-col items-start gap-0.5 px-3 py-3 text-left"
									}
									onClick={() => openAction(a.key)}
								>
									<Icon className="size-4" aria-hidden />
									<span className="text-xs font-semibold">{a.label}</span>
									<span className="text-[10px] opacity-80">{a.desc}</span>
								</button>
							);
						})}
					</div>
					{isEmptyVault ? (
						<div className="rounded-xl border border-warning/40 bg-warning/10 p-3">
							<p className="text-xs text-muted-foreground">
								This vault is empty (0 oracle value, 0.00 USDM debt). Same as
								after liquidation cleanup — there is no reason to keep it.
							</p>
							<button
								type="button"
								className="btn-secondary mt-2 inline-flex w-full items-center justify-center gap-2 py-2.5 text-sm text-danger"
								onClick={() => setCloseOpen(true)}
							>
								<Trash2 className="size-4" aria-hidden />
								Delete empty vault
							</button>
						</div>
					) : null}
				</section>
			) : (
				<section className="space-y-3 text-center">
					<p className="text-xs text-muted-foreground">
						Public view — only the owner can deposit, withdraw, mint, or repay.
						{vault.owner ? (
							<span className="mt-1 block font-mono text-[10px]">
								Owner {vault.owner.slice(0, 8)}…{vault.owner.slice(-6)}
							</span>
						) : null}
					</p>
					{(() => {
						const risk = classifyVaultRisk(
							vault.collateralUsd,
							vault.debtUsdm,
							riskParams,
						);
						if (
							risk.band === "liquidatable" ||
							risk.band === "underwater"
						) {
							return (
								<Link
									to={ROUTES.liquidation}
									className="btn-primary inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm"
								>
									<Gavel className="size-4" aria-hidden />
									{risk.band === "underwater"
										? "Open Dutch auction / Liquidate"
										: "Liquidate this vault"}
								</Link>
							);
						}
						return null;
					})()}
				</section>
			)}

			<ConfirmSheet
				open={action !== null}
				onOpenChange={(o) => {
					if (!o && !pending) {
						setAction(null);
						setStage("idle");
					}
				}}
				title={titleFor(action)}
				description={descFor(action)}
				confirmLabel={
					action === "deposit"
						? "Confirm deposit"
						: action === "withdraw"
							? "Confirm withdraw"
							: action === "mint"
								? "Confirm mint"
								: "Confirm repay"
				}
				onConfirm={() => void runAction()}
				isPending={pending}
				confirmDisabled={
					!amount ||
					n <= 0 ||
					(action === "deposit" && n > walletCol + 1e-12) ||
					(action === "withdraw" && n > maxWithdrawToken + 1e-12) ||
					(action === "mint" && n > maxMint + 1e-12) ||
					(action === "repay" && n > maxRepay + 1e-12)
				}
				stage={stage}
				destructive={action === "repay" || action === "withdraw"}
			>
				{action === "deposit" || action === "withdraw" ? (
					<div className="space-y-2">
						<p className="text-xs font-semibold text-muted-foreground">Asset</p>
						<div className="flex gap-2">
							{COLS.map((k) => (
								<button
									key={k}
									type="button"
									className={
										collateral === k
											? "btn-primary flex flex-1 flex-col items-center gap-1 !min-h-14 px-2 text-xs"
											: "btn-secondary flex flex-1 flex-col items-center gap-1 !min-h-14 px-2 text-xs"
									}
									onClick={() => setCollateral(k)}
								>
									<TokenIcon token={k} size="md" plain />
									{k}
								</button>
							))}
						</div>
					</div>
				) : null}

				<label className="block">
					<span className="text-xs font-semibold text-muted-foreground">
						Amount
					</span>
					<div className="mt-1 flex items-center gap-2">
						<input
							value={amount}
							onChange={(e) => setAmount(e.target.value)}
							inputMode="decimal"
							placeholder="0.00"
							className="w-full rounded-xl border border-border bg-muted/40 px-4 py-3 font-mono-amount text-lg outline-none focus:border-primary"
						/>
						{action === "deposit" || action === "withdraw" ? (
							<TokenLabel id={collateral} size="sm" />
						) : (
							<TokenLabel id="USDM" size="sm" />
						)}
					</div>
				</label>

				{action === "deposit" ? (
					<>
						<p className="text-xs text-muted-foreground">
							Spendable{" "}
							<span className="balance-chip">
								{formatNum(walletCol, colDecimals)} {collateral}
							</span>{" "}
							<span className="text-[10px]">(wallet − transfer fee)</span>
						</p>
						<RangeSlider
							id="vault-deposit-pct"
							label="Percent of spendable"
							valueLabel={`${pctOf(n, walletCol).toFixed(1)}%`}
							min={0}
							max={100}
							step={0.1}
							disabled={walletCol <= 0}
							value={pctOf(n, walletCol)}
							onChange={(pct) =>
								setAmount(
									walletCol > 0
										? formatAmountPlain((walletCol * pct) / 100, colDecimals)
										: "0",
								)
							}
						/>
					</>
				) : null}

				{action === "withdraw" ? (
					<>
						<p className="text-xs text-muted-foreground">
							Max free ≈{" "}
							<span className="font-mono-amount font-semibold">
								{formatNum(maxWithdrawToken, colDecimals)} {collateral}
							</span>{" "}
							(CR ≥ {minCr.toFixed(2)}×)
						</p>
						<RangeSlider
							id="vault-withdraw-pct"
							label="Percent of free collateral"
							valueLabel={`${pctOf(n, maxWithdrawToken).toFixed(1)}%`}
							min={0}
							max={100}
							step={0.1}
							disabled={maxWithdrawToken <= 0}
							value={pctOf(n, maxWithdrawToken)}
							onChange={(pct) =>
								setAmount(
									maxWithdrawToken > 0
										? formatAmountPlain(
												(maxWithdrawToken * pct) / 100,
												colDecimals,
											)
										: "0",
								)
							}
						/>
					</>
				) : null}

				{action === "mint" ? (
					<>
						<p className="text-xs text-muted-foreground">
							Max mint ≈{" "}
							<TokenAmount id="USDM" value={maxMint} decimals={2} size="sm" />{" "}
							<span className="text-[10px]">(includes CR buffer)</span>
						</p>
						<RangeSlider
							id="vault-mint-pct"
							label="Percent of max mint"
							valueLabel={`${pctOf(n, maxMint).toFixed(1)}%`}
							min={0}
							max={100}
							step={0.1}
							disabled={maxMint <= 0}
							value={pctOf(n, maxMint)}
							onChange={(pct) =>
								setAmount(
									maxMint > 0
										? formatAmountPlain((maxMint * pct) / 100, 2)
										: "0",
								)
							}
						/>
					</>
				) : null}

				{action === "repay" ? (
					<>
						<p className="text-xs text-muted-foreground">
							Max repay{" "}
							<span className="balance-chip">
								{formatNum(maxRepay, 2)} USDM
							</span>{" "}
							<span className="text-[10px]">(debt ∩ wallet − fee)</span>
						</p>
						<RangeSlider
							id="vault-repay-pct"
							label="Percent of max repay"
							valueLabel={`${pctOf(n, maxRepay).toFixed(1)}%`}
							min={0}
							max={100}
							step={0.1}
							disabled={maxRepay <= 0}
							value={pctOf(n, maxRepay)}
							onChange={(pct) =>
								setAmount(
									maxRepay > 0
										? formatAmountPlain((maxRepay * pct) / 100, 2)
										: "0",
								)
							}
						/>
					</>
				) : null}

				{preview ? (
					<div className="rounded-xl border border-border bg-muted/30 p-3 text-xs">
						<p className="mb-2 font-semibold">After action</p>
						<div className="grid grid-cols-2 gap-2">
							<div>
								<span className="text-muted-foreground">Collateral</span>
								<p className="font-mono-amount font-semibold">
									{formatUsd(preview.col, 0)}
								</p>
							</div>
							<div>
								<span className="text-muted-foreground">Debt</span>
								<TokenAmount id="USDM" value={preview.debt} decimals={2} size="sm" />
							</div>
							<div>
								<span className="text-muted-foreground">LTV</span>
								<p className="font-mono-amount font-semibold">
									{preview.ltv.toFixed(1)}%
								</p>
							</div>
							<div>
								<span className="text-muted-foreground">Health</span>
								<p className="font-mono-amount font-semibold">
									{Number.isFinite(preview.hf)
										? `${preview.hf.toFixed(2)}×`
										: "∞"}
								</p>
							</div>
						</div>
						{preview.ltv > maxMintLtv ? (
							<p className="mt-2 text-danger">
								Warning: LTV above ~{maxMintLtv.toFixed(0)}% may be
								liquidatable.
							</p>
						) : null}
						{willBeEmptyAfter ? (
							<p className="mt-2 text-warning">
								Vault will be empty after this. You can delete it next (same as
								liquidated positions).
							</p>
						) : null}
					</div>
				) : null}
			</ConfirmSheet>

			<ConfirmSheet
				open={closeOpen}
				onOpenChange={(o) => {
					if (!closing) setCloseOpen(o);
				}}
				title="Delete empty vault"
				description="Removes this vault on-chain. Only allowed when debt is 0 and all collateral is withdrawn (oracle value $0)."
				confirmLabel={closing ? "Deleting…" : "Delete vault"}
				onConfirm={() => void runCloseEmpty()}
				isPending={closing}
				destructive
			>
				<ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
					<li>0.00 USDM debt</li>
					<li>No remaining collateral tokens</li>
					<li>Cannot be undone — open a new vault via Borrow when needed</li>
				</ul>
			</ConfirmSheet>
		</div>
	);
}
